import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyCaravaggioMode({ brand, today, Copy }: ModeRendererProps) {
  // stable id base from URL (id/uid/modeId/mode_id) or random fallback
  const idBase = React.useMemo(() => {
    try {
      const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
      const param = url
        ? url.searchParams.get("id") || url.searchParams.get("uid") || url.searchParams.get("modeId") || url.searchParams.get("mode_id")
        : null;
      return param ? `caravaggio_${param}` : `caravaggio_${Math.random().toString(36).slice(2, 8)}`;
    } catch {
      return `caravaggio_${Math.random().toString(36).slice(2, 8)}`;
    }
  }, []);

  const gradId = `${idBase}_grad`;
  const vignetteId = `${idBase}_vignette`;

  // painterly strokes
  const strokes: React.ReactNode[] = [];
  for (let i = 0; i < 10; i++) {
    const x = 150 + i * 60;
    const y = 200 + (i % 3) * 40;
    const w = 220 - i * 10;
    const h = 40 + (i % 4) * 6;
    strokes.push(
      <rect
        key={`stroke-${i}`}
        x={x}
        y={y}
        width={w}
        height={h}
        rx={8}
        fill={`url(#${gradId})`}
        opacity={0.06 + (i % 3) * 0.06}
        transform={`rotate(${(i - 4.5) * 3}, ${x + w / 2}, ${y + h / 2})`}
      />
    );
  }

  const spotlights: React.ReactNode[] = [];
  const centers = [ {x: 330, y: 380, rX: 180, rY: 260, o: 0.95}, {x: 520, y: 520, rX: 160, rY: 220, o:0.6} ];
  centers.forEach((c, idx) => {
    spotlights.push(
      <ellipse
        key={`spot-${idx}`}
        cx={c.x}
        cy={c.y}
        rx={c.rX}
        ry={c.rY}
        fill={`url(#${gradId})`}
        opacity={c.o}
      />
    );
  });

  const shadows: React.ReactNode[] = [];
  for (let s = 0; s < 6; s++) {
    const x = 40 + s * 110;
    shadows.push(
      <rect
        key={`shadow-${s}`}
        x={x}
        y={640 + (s % 2) * 20}
        width={220}
        height={280}
        fill="#070403"
        opacity={0.24 - s * 0.02}
        transform={`skewX(${(s - 2.5) * 2})`}
      />
    );
  }

  return (
    <g>
      <defs>
        <radialGradient id={gradId} cx="40%" cy="30%">
          <stop offset="0%" stopColor="#fff8e6" stopOpacity={0.95} />
          <stop offset="45%" stopColor="#e0cfa0" stopOpacity={0.7} />
          <stop offset="75%" stopColor="#6b4f2b" stopOpacity={0.6} />
          <stop offset="100%" stopColor="#0b0503" stopOpacity={1} />
        </radialGradient>
        <filter id={vignetteId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="40" result="g" />
          <feComponentTransfer in="g">
            <feFuncA type="table" tableValues="0 0.6" />
          </feComponentTransfer>
          <feComposite in2="SourceGraphic" operator="over" />
        </filter>
      </defs>

      {/* Full dark background */}
      <rect width={794} height={1123} fill="#080504" />

      <g filter={`url(#${vignetteId})`}>
        {spotlights}
      </g>

      <g opacity={0.95}>{strokes}</g>

      <g>{shadows}</g>

      <g>
        <circle cx={420} cy={420} r={160} fill={`url(#${gradId})`} opacity={0.98} />
        <circle cx={420} cy={420} r={110} fill="none" stroke="#2b1b11" strokeWidth={2} opacity={0.6} />
        <text x={420} y={428} fontFamily="serif" fontSize={48} fontWeight="700" fill="#0b0503" textAnchor="middle">
          {"CARAVAGGIO"}
        </text>
      </g>

      <g className="mode-copy" transform="translate(48, 840)" fill="#EDE0C8" fontFamily="serif">
        <text x={0} y={0} fontSize={18} fontWeight="700">
          {Copy?.title}
        </text>
        <text x={0} y={22} fontSize={14}>
          {Copy?.subtitle}
        </text>
        <text x={0} y={44} fontSize={12} opacity={0.9}>
          {Copy?.date}
        </text>
      </g>
    </g>
  );
}

export default VasarelyCaravaggioMode;

